import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./OtherPaymentHistory.css";
import { otherChargePaymentService } from "service/otherChargePaymentService";

const DEFAULT_FROM = "2024-12-01";
const DEFAULT_TO = new Date().toISOString().slice(0, 10);

// Map a service name to a distinct badge color so rows are scannable at a glance
const badgeClassForService = (service = "") => {
  const s = service.toUpperCase();
  if (s.includes("WHATSAPP")) return "opay-badge-whatsapp";
  if (s.includes("SMS")) return "opay-badge-sms";
  if (s.includes("SSL")) return "opay-badge-ssl";
  return "opay-badge-default";
};

// Format a raw numeric string like "299.0000" into "₹299.00"
const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value || "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const isRegistered = (value) => value && value !== "Not Register" && value !== "";

const OtherPaymentHistory = () => {
  const [paymentData, setPaymentData] = useState([]);
  const [serviceStatus, setServiceStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fromDate, setFromDate] = useState(DEFAULT_FROM);
  const [toDate, setToDate] = useState(DEFAULT_TO);
  const [appliedRange, setAppliedRange] = useState({ from: DEFAULT_FROM, to: DEFAULT_TO });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await otherChargePaymentService.getHistory();
      setPaymentData(Array.isArray(res?.data) ? res.data : []);
      setServiceStatus(res?.service_status || {});
    } catch (err) {
      setError(err.message || "Failed to load service payment history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Parse "dd-mm-yyyy hh:mm:ss" into a comparable Date
  const parsePurchaseDate = (value) => {
    const [datePart] = value.split(" ");
    const [day, month, year] = datePart.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const filteredData = useMemo(() => {
    const from = new Date(appliedRange.from);
    const to = new Date(appliedRange.to);
    to.setHours(23, 59, 59, 999);

    return paymentData
      .filter((item) => {
        const purchaseDate = parsePurchaseDate(item.purchase);
        return purchaseDate >= from && purchaseDate <= to;
      })
      .sort((a, b) => parsePurchaseDate(b.purchase) - parsePurchaseDate(a.purchase));
  }, [paymentData, appliedRange]);

  const handleSearch = () => {
    setAppliedRange({ from: fromDate, to: toDate });
  };

  return (
    <div className="opay-container mt-3">
      {/* Top Header Filter Bar */}
      <div className="opay-header-bar">
        <div className="opay-left">
          <i className="iconoir-home-alt opay-home-icon" aria-hidden="true"></i>
          <label className="opay-breadcrumb-label">
            <input type="checkbox" defaultChecked={false} className="opay-checkbox" />
            History
          </label>
          <label className="opay-breadcrumb-label">
            <input type="checkbox" defaultChecked={false} className="opay-checkbox" />
            Other Services
          </label>
          {loading && (
            <div className="spinner-border spinner-border-sm text-primary ms-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>
        <div className="opay-right">
          <input
            type="date"
            className="opay-date-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <span className="opay-arrow">⇄</span>
          <input
            type="date"
            className="opay-date-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <button className="opay-search-btn" onClick={handleSearch}>
            <i className="ti ti-search opay-search-icon"></i> Search
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Service Outlined Status Boxes */}
      <div className="opay-status-grid">
        {Object.entries(serviceStatus).map(([label, value]) => (
          <div
            className={`opay-status-box ${isRegistered(value) ? "is-active" : "is-inactive"}`}
            key={label}
          >
            <span className="opay-status-label">{label}</span>
            <span className="opay-status-value">
              {isRegistered(value) ? value : "Not Register"}
            </span>
          </div>
        ))}
      </div>

      {/* Main Table Card */}
      <div className="opay-table-card mt-3">
        <div className="opay-table-toolbar">
          <span className="opay-table-title">Other Service Payments</span>
          <span className="opay-table-count">
            {filteredData.length} {filteredData.length === 1 ? "record" : "records"}
          </span>
        </div>
        <div className="table-responsive">
          <table className="table opay-table">
            <thead>
              <tr>
                <th className="text-center" style={{ width: "40px" }}>
                  <input type="checkbox" className="custom-table-checkbox" />
                </th>
                <th>User Name</th>
                <th>Service Name</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Pre(₹)</th>
                <th>Total Pay</th>
                <th>Gst</th>
                <th>Post(₹)</th>
                <th>Purchase Time</th>
                <th>Exp Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      <input type="checkbox" className="custom-table-checkbox" />
                    </td>
                    <td className="text-start">{item.user}</td>
                    <td>
                      <span className={`opay-badge ${badgeClassForService(item.service)}`}>
                        {item.service}
                      </span>
                    </td>
                    <td>{item.quantity || "-"}</td>
                    <td className="opay-amount">{formatCurrency(item.price)}</td>
                    <td className="opay-amount">{formatCurrency(item.pre)}</td>
                    <td className="opay-amount">{formatCurrency(item.total)}</td>
                    <td className="opay-amount">{formatCurrency(item.gst)}</td>
                    <td className="opay-amount is-post">{formatCurrency(item.post)}</td>
                    <td>{item.purchase}</td>
                    <td>{item.expiry || "-"}</td>
                    <td>
                      <span
                        className={`opay-status-badge ${
                          item.status === "Approved" ? "opay-status-approved" : "opay-status-pending"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={12} className="opay-no-data">
                    {loading ? "Loading…" : "No records found for the selected date range."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OtherPaymentHistory;